// Horizontal bar showing market salary p25/p50/p75 with the user's offered
// total marked. Anchored at p25 (left) ↔ p75 (right) with 15% padding so a
// user offer below p25 or above p75 still has visual room. p50 marker is
// drawn as a vertical tick inside the green band.
//
// Color logic for the user marker:
//   below p25 → red
//   p25 to p50 → amber
//   p50 to p75 → ink (regular)
//   above p75 → green
//
// AI-estimate disclaimer ALWAYS visible — these numbers come from the LLM,
// not Levels.fyi/Glassdoor, so user must spot-check.

import { Sparkles, AlertCircle, RefreshCw } from 'lucide-react';
import type { SalaryBenchmark } from '../../lib/types';

interface Props {
  totalK: number;                      // user's current total comp in $k
  benchmark: SalaryBenchmark | null;
  isLoading: boolean;
  isStale: boolean;
  error: string | null;
  onFetch: () => void;
  onRefresh?: () => void;              // shown when we have a result + want to re-run
}

const PAD_FRAC = 0.15;                 // visual padding outside p25-p75 band

export default function MarketBar({
  totalK, benchmark, isLoading, isStale, error, onFetch, onRefresh,
}: Props) {
  // No benchmark yet — surface a CTA, not a placeholder bar
  if (!benchmark && !isLoading) {
    return (
      <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(120, 90, 200, 0.04)', border: '1px dashed var(--line2)', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Sparkles size={12} style={{ color: 'var(--pri)', flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 12, color: 'var(--ink2)' }}>
          {error ? error : 'No market estimate yet for this role.'}
        </span>
        <button
          className="btn"
          onClick={onFetch}
          style={{ fontSize: 11, padding: '3px 10px' }}
          disabled={isLoading}
          title="Ask the local LLM to estimate p25/p50/p75 from training-data knowledge of comparable roles"
        >
          Estimate market
        </button>
      </div>
    );
  }

  if (!benchmark && isLoading) {
    return (
      <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(120, 90, 200, 0.04)', border: '1px dashed rgba(120,90,200,0.3)', borderRadius: 6, fontSize: 12, color: 'var(--ink2)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Sparkles size={12} className="spin" style={{ color: 'var(--pri)' }} />
        Estimating market rate (~10s)…
      </div>
    );
  }

  // We have a benchmark — render the bar
  const { p25, p50, p75, confidence, reasoning, currency } = benchmark!;

  // Layout extents: left edge 15% below p25, right edge 15% above p75
  const range = p75 - p25;
  const left  = p25 - range * PAD_FRAC;
  const right = p75 + range * PAD_FRAC;
  const span  = right - left;
  const pct = (val: number) => Math.max(0, Math.min(100, ((val - left) / span) * 100));

  const userPct = pct(totalK);
  const p25Pct = pct(p25);
  const p50Pct = pct(p50);
  const p75Pct = pct(p75);

  const userColor = totalK < p25 ? '#b43a3a'
    : totalK < p50 ? '#c08736'
    : totalK < p75 ? 'var(--ink)'
    : '#2d7a4a';

  const userLabel = totalK < p25 ? 'below p25'
    : totalK < p50 ? `between p25-p50`
    : totalK < p75 ? `between p50-p75`
    : 'above p75';

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--ink3)', flex: 1 }}>
          Market estimate · ${currency} · confidence {confidence}
        </span>
        {onRefresh && !isLoading && (
          <button
            className="btn ghost"
            onClick={onRefresh}
            style={{ fontSize: 10.5, padding: '1px 6px', display: 'inline-flex', alignItems: 'center', gap: 3 }}
            title={isStale ? 'Cached estimate is older than 7 days — refresh' : 'Re-run the LLM estimate'}
          >
            <RefreshCw size={9} /> {isLoading ? 'Refreshing' : 'Refresh'}
          </button>
        )}
      </div>

      {/* The bar itself */}
      <div style={{ position: 'relative', height: 28 }}>
        {/* Light track */}
        <div style={{ position: 'absolute', top: 12, left: 0, right: 0, height: 4, background: 'var(--line)', borderRadius: 2 }} />
        {/* Green band p25 → p75 */}
        <div
          style={{
            position: 'absolute',
            top: 11,
            left: `${p25Pct}%`,
            width: `${p75Pct - p25Pct}%`,
            height: 6,
            background: 'rgba(45, 122, 74, 0.32)',
            borderRadius: 3,
          }}
        />
        {/* p50 tick */}
        <div
          title={`p50 (median): $${p50}k`}
          style={{
            position: 'absolute',
            top: 8,
            left: `${p50Pct}%`,
            width: 2,
            height: 12,
            background: '#2d7a4a',
            transform: 'translateX(-50%)',
          }}
        />
        {/* p25 + p75 ticks (lighter) */}
        {[
          { pct: p25Pct, label: `p25 $${p25}k` },
          { pct: p75Pct, label: `p75 $${p75}k` },
        ].map((t) => (
          <div
            key={t.label}
            title={t.label}
            style={{
              position: 'absolute',
              top: 10,
              left: `${t.pct}%`,
              width: 1.5,
              height: 8,
              background: 'rgba(45,122,74,0.6)',
              transform: 'translateX(-50%)',
            }}
          />
        ))}
        {/* User offer marker — circle on track */}
        <div
          title={`Your offer: $${totalK}k (${userLabel})`}
          style={{
            position: 'absolute',
            top: 7,
            left: `${userPct}%`,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: userColor,
            border: '2.5px solid var(--bg)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        />
      </div>

      {/* Numbers row under the bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--ink3)', marginTop: 4 }}>
        <span>p25 ${p25}k</span>
        <span style={{ color: '#2d7a4a', fontWeight: 500 }}>p50 ${p50}k</span>
        <span>p75 ${p75}k</span>
      </div>

      {/* User-offer summary line */}
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink2)' }}>
        <span style={{ color: userColor, fontWeight: 600 }}>${totalK}k</span>
        {' '}· {userLabel}
        {totalK < p50 && (
          <span style={{ color: 'var(--warn)', marginLeft: 6 }}>
            ($ {p50 - totalK}k below median — leverage to negotiate)
          </span>
        )}
      </div>

      {/* Reasoning + AI-estimate disclaimer */}
      {reasoning && (
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ink3)', fontStyle: 'italic', lineHeight: 1.5 }}>
          {reasoning}
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: 10.5, color: 'var(--ink3)', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
        <AlertCircle size={10} style={{ marginTop: 2, flexShrink: 0 }} />
        <span>
          AI estimate from local LLM. Verify on{' '}
          <a href="https://www.levels.fyi" target="_blank" rel="noreferrer" style={{ color: 'var(--pri)' }}>levels.fyi</a>{' / '}
          <a href="https://www.glassdoor.com/Salaries/index.htm" target="_blank" rel="noreferrer" style={{ color: 'var(--pri)' }}>glassdoor</a>{' '}
          before hard-anchoring a counter.
        </span>
      </div>
    </div>
  );
}
